import { useMemo } from 'react'
import type {
  HistoryEvent,
  PermitEvent,
  TransactionEvent,
  ListingEvent,
} from '../../../../../../molecules/HistoryTab'

// Helper to safely convert date to ISO string
const toISOString = (date: Date | string | undefined): string => {
  if (!date) return ''
  if (typeof date === 'string') return date
  return date.toISOString()
}

/**
 * Hook that transforms raw history data into HistoryEvent[] format.
 * Works with both land and building analysis data (same history shape).
 *
 * @param entity - The entity object containing a `.history` field
 */
export const useHistoryData = (entity: any) => {
  const historyData = entity?.history

  const events = useMemo(() => {
    const historyEvents: HistoryEvent[] = []

    // Process building permits
    if (historyData?.buildingPermits && Array.isArray(historyData.buildingPermits)) {
      historyData.buildingPermits.forEach((permit: any, index: number) => {
        const permitEvent: PermitEvent = {
          ...permit,
          id: `permit-${permit.id || index}`,
          date: toISOString(permit.inquiry_start_date || permit.inquiry_last_date),
          type: 'permit',
          workDescription: permit.work_description || permit.description,
          permitNumber: permit.primary_official_id || permit.id?.toString(),
          workType: permit.work_type_official || permit.type_name,
          developmentPotential: permit.development_classification_llm,
          projectArea: permit.lands?.[0]?.area,
          classification: permit.regbl_classification_general,
          inquiryStartDate: toISOString(permit.inquiry_start_date),
          inquiryLastDate: toISOString(permit.inquiry_last_date),
          participantsNumber: permit.participants?.length,
          owner: permit.owner,
        }
        historyEvents.push(permitEvent)
      })
    }

    // Process transactions
    if (historyData?.transactions && Array.isArray(historyData.transactions)) {
      historyData.transactions.forEach((transaction: any, index: number) => {
        const transactionEvent: TransactionEvent = {
          ...transaction,
          id: transaction.id || `transaction-${index}`,
          date: transaction.transaction_date || transaction.date || '',
          type: 'transaction',
          transaction_id: transaction.transaction_id || String(transaction.id || index),
          official_id: transaction.official_id,
          transaction_date: transaction.transaction_date || transaction.date,
          transaction_total_price: transaction.transaction_total_price || transaction.price || 0,
          transaction_step_total_price: transaction.transaction_step_total_price,
          transaction_step_name: transaction.transaction_step_name,
          transaction_step_text: transaction.transaction_step_text,
          buyers_count: transaction.buyers_count || transaction.buyers_nb || 0,
          sellers_count: transaction.sellers_count || transaction.sellers_nb || 0,
          properties_count:
            transaction.properties_count || transaction.transaction_properties?.length || 0,
          transaction_participants: transaction.transaction_participants || [],
          transaction_properties: transaction.transaction_properties || [],
        }
        historyEvents.push(transactionEvent)
      })
    }

    // Process listings
    if (historyData?.listings && Array.isArray(historyData.listings)) {
      historyData.listings.forEach((listing: any, index: number) => {
        const isRental = listing.deal_type === 'rent'

        if (isRental) {
          const rentalEvent: ListingEvent = {
            id: `rental-${listing.id || index}`,
            date: listing.listing_timestamp || listing.active_listing_timestamp || '',
            type: 'rental',
            price: listing.price || 0,
            pricePerSqm: listing.price_per_square_meter,
            address: listing.address,
            propertyType: listing.property_type,
            propertyCategory: listing.property_category,
            area: listing.area,
            rooms: listing.rooms_nb,
            bathrooms: listing.bathroom_nb,
            floor: listing.floor,
            constructionYear: listing.construction_year,
            description: listing.description?.replace(/<[^>]*>/g, ''),
            status: listing.ad_status ? 'Active' : 'Inactive',
            ad_status: Boolean(listing.ad_status),
            onlineDays: listing.listing_duration,
            imageUrls: listing.urls_image || [],
            brokerName: listing.broker_info?.contact_names?.[0]?.name,
            brokersInfo: listing.broker_info,
            availableDate: listing.active_listing_timestamp,
            furnished: listing.features?.includes('Furnished'),
            petsAllowed: listing.features?.includes('Pets allowed'),
            parking: listing.features?.includes('Parking'),
            balcony: listing.features?.includes('Balcony'),
            terrace: listing.features?.includes('Terrace'),
            garden: listing.features?.includes('Garden'),
            features: listing.features,
          }
          historyEvents.push(rentalEvent)
        } else {
          const saleEvent: ListingEvent = {
            id: `sale-${listing.id || index}`,
            date: listing.listing_timestamp || listing.active_listing_timestamp || '',
            type: 'sale',
            price: listing.price || 0,
            pricePerSqm: listing.price_per_square_meter,
            address: listing.address,
            propertyType: listing.property_type,
            propertyCategory: listing.property_category,
            area: listing.area,
            landArea: listing.land_area,
            rooms: listing.rooms_nb,
            bathrooms: listing.bathroom_nb,
            floor: listing.floor,
            constructionYear: listing.construction_year,
            description: listing.description?.replace(/<[^>]*>/g, ''),
            status: listing.ad_status ? 'Active' : 'Inactive',
            ad_status: Boolean(listing.ad_status),
            onlineDays: listing.listing_duration,
            imageUrls: listing.urls_image || [],
            brokerName: listing.broker_info?.contact_names?.[0]?.name,
            brokersInfo: listing.broker_info,
            garage: listing.features?.includes('Garage'),
            parking: listing.features?.includes('Parking'),
            balcony: listing.features?.includes('Balcony'),
            terrace: listing.features?.includes('Terrace'),
            garden: listing.features?.includes('Garden'),
            elevator: listing.features?.includes('Elevator'),
            features: listing.features,
          }
          historyEvents.push(saleEvent)
        }
      })
    }

    // Sort by date (newest first)
    return historyEvents.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  }, [historyData])

  return {
    events,
    loading: false,
    error: null,
  }
}
